import { create } from 'zustand';
import {
  updateLastMessage,
  markChatAsRead,
  updateParticipantStatus,
  sortChatsByLastMessage,
} from './chat.lib';
import { USER_STATUS } from '../api/ws.types';

export const useChatStore = create((set, get) => ({
  // State
  chats: [],
  selectedChat: null,
  messageHistory: [],
  messageIdsSet: new Set(),
  isEnd: false,
  prevSelectedChat: null,
  wsConnected: false,
  wsReconnecting: false,
  historyLoading: false,
  historyError: null,
  chatsLoading: true,
  lastPongAt: null,
  // Связь id WS-команды с оптимистичным сообщением (для обработки ошибок Reply)
  commandTempIds: {},

  // Actions
  setChats: (chats) => set({ chats: sortChatsByLastMessage(chats), chatsLoading: false }),

  selectChat: (chat) =>
    set({
      selectedChat: chat,
      prevSelectedChat: get().selectedChat,
      messageHistory: [],
      messageIdsSet: new Set(),
      isEnd: false,
      historyLoading: false,
    }),

  setPrevSelectedChat: (chat) => set({ prevSelectedChat: chat }),

  appendHistory: (messages) =>
    set((state) => {
      const newMessages = messages.filter((m) => !state.messageIdsSet.has(m.id));
      if (newMessages.length === 0) {
        return { isEnd: messages.length === 0, historyLoading: false };
      }
      const nextSet = new Set(state.messageIdsSet);
      newMessages.forEach((m) => nextSet.add(m.id));
      return {
        messageHistory: [...state.messageHistory, ...newMessages],
        messageIdsSet: nextSet,
        isEnd: messages.length === 0,
        historyLoading: false,
      };
    }),

  setHistory: (messages) =>
    set(() => {
      const nextSet = new Set();
      messages.forEach((m) => nextSet.add(m.id));
      return {
        messageHistory: messages,
        messageIdsSet: nextSet,
        isEnd: messages.length === 0,
        historyLoading: false,
      };
    }),

  prependMessage: (message) =>
    set((state) => {
      if (state.messageIdsSet.has(message.id)) {
        return {};
      }
      const nextSet = new Set(state.messageIdsSet);
      nextSet.add(message.id);
      return {
        messageHistory: [message, ...state.messageHistory],
        messageIdsSet: nextSet,
        chats: sortChatsByLastMessage(updateLastMessage(state.chats, message)),
      };
    }),

  prependMessageToChat: (message) =>
    set((state) => ({
      chats: sortChatsByLastMessage(updateLastMessage(state.chats, message)),
    })),

  // Оптимистичное обновление
  addOptimisticMessage: (message) =>
    set((state) => {
      const nextSet = new Set(state.messageIdsSet);
      nextSet.add(message.id);
      return {
        messageHistory: [message, ...state.messageHistory],
        messageIdsSet: nextSet,
      };
    }),

  confirmOptimisticMessage: (tempId, realMessage) =>
    set((state) => {
      const nextSet = new Set(state.messageIdsSet);
      nextSet.add(realMessage.id);
      return {
        messageHistory: state.messageHistory.map((m) =>
          m.id === tempId ? { ...realMessage, pending: false, failed: false } : m
        ),
        messageIdsSet: nextSet,
        chats: sortChatsByLastMessage(updateLastMessage(state.chats, realMessage)),
      };
    }),

  failOptimisticMessage: (tempId) =>
    set((state) => ({
      messageHistory: state.messageHistory.map((m) =>
        m.id === tempId ? { ...m, pending: false, failed: true } : m
      ),
    })),

  removeOptimisticMessage: (tempId) =>
    set((state) => {
      const nextSet = new Set(state.messageIdsSet);
      nextSet.delete(tempId);
      return {
        messageHistory: state.messageHistory.filter((m) => m.id !== tempId),
        messageIdsSet: nextSet,
      };
    }),

  markAsRead: (chatId, messageId) =>
    set((state) => ({
      chats: markChatAsRead(state.chats, chatId, messageId),
      messageHistory: state.messageHistory.map((msg) =>
        msg.id === messageId ? { ...msg, is_read: true } : msg
      ),
    })),

  updateUserStatus: (userId, status) =>
    set((state) => {
      const updatedChats = updateParticipantStatus(state.chats, userId, status);
      const isOnline = status === USER_STATUS.ONLINE;

      const selectedParticipant = state.selectedChat?.participants?.find(
        (p) => p.id === userId
      );
      const selectedChanged =
        selectedParticipant && selectedParticipant.online !== isOnline;

      if (updatedChats === state.chats && !selectedChanged) {
        return {};
      }

      const updatedSelected = selectedChanged
        ? {
            ...state.selectedChat,
            participants: state.selectedChat.participants.map((p) =>
              p.id === userId ? { ...p, online: isOnline } : p
            ),
          }
        : state.selectedChat;

      return {
        chats: updatedChats === state.chats ? state.chats : updatedChats,
        selectedChat: updatedSelected,
      };
    }),

  resetHistory: () =>
    set({
      messageHistory: [],
      messageIdsSet: new Set(),
      isEnd: false,
      historyLoading: false,
      historyError: null,
    }),

  setWsConnected: (connected) => set({ wsConnected: connected }),

  registerCommandTempId: (commandId, tempId) =>
    set((state) => ({
      commandTempIds: { ...state.commandTempIds, [commandId]: tempId },
    })),

  // Забирает tempId по id команды (и удаляет связь). undefined — связи не было.
  takeCommandTempId: (commandId) => {
    const tempId = get().commandTempIds[commandId];
    if (tempId === undefined) return undefined;
    set((state) => {
      const next = { ...state.commandTempIds };
      delete next[commandId];
      return { commandTempIds: next };
    });
    return tempId;
  },

  // id самого старого pending-текстового сообщения от senderId в чате.
  // Нужно, когда сервер изменил текст сообщения (например, скрыл личные
  // данные) и матчинг по содержимому не сработал.
  takeOldestPendingTextId: (chatId, senderId) => {
    const pending = [...get().messageHistory]
      .reverse()
      .find(
        (m) =>
          m.pending &&
          m.chat_id === chatId &&
          m.sender_id === senderId &&
          (!m.type || m.type === 'text')
      );
    return pending?.id;
  },
  setWsReconnecting: (reconnecting) => set({ wsReconnecting: reconnecting }),
  setHistoryLoading: (loading) => set({ historyLoading: loading }),
  setHistoryError: (error) => set({ historyError: error }),
  setLastPongAt: (time) => set({ lastPongAt: time }),
}));
